import React from "react";
import styled from "styled-components";

const CONSTRUCTION_DASHBOARD_URL = "https://universal-construction-dashboard.onrender.com";

const Section = styled.section`
  padding: 4.5rem 2rem;
  background: #f8fafc;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
  @media (max-width: 480px) {
    padding: 2.5rem 1rem;
  }
`;

const Card = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(280px, 440px) 1fr;
  align-items: center;
  gap: clamp(1.5rem, 4vw, 3rem);
  background: linear-gradient(135deg, #2a5a7a 0%, #17384d 100%);
  border-radius: 28px;
  padding: clamp(1.75rem, 4vw, 3rem);
  box-shadow: 0 24px 60px rgba(23, 56, 77, 0.28);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
    justify-items: center;
  }
`;

/* ===== Infographic illustration ===== */
const Illustration = styled.div`
  width: 100%;
  max-width: 420px;
`;

const TextBlock = styled.div`
  max-width: 560px;
`;

const Title = styled.h2`
  color: #fff;
  margin: 0 0 0.7rem;
  font-weight: 800;
  font-size: clamp(1.5rem, 2.8vw, 2.1rem);
  line-height: 1.2;
`;

const Desc = styled.p`
  color: #cbd8e0;
  margin: 0 0 1.4rem;
  font-size: 1rem;
  line-height: 1.65;
`;

const Features = styled.ul`
  list-style: none;
  margin: 0 0 1.8rem;
  padding: 0;
  display: grid;
  gap: 0.75rem;

  @media (max-width: 900px) {
    justify-items: center;
  }
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: #e7eef3;
  font-weight: 600;
  font-size: 0.95rem;
`;

const FeatureIcon = styled.span`
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: rgba(61, 204, 199, 0.18);
  border: 1px solid rgba(61, 204, 199, 0.35);
  flex-shrink: 0;
`;

const CTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  padding: 1rem 1.6rem;
  border-radius: 12px;
  white-space: nowrap;
  box-shadow: 0 10px 24px rgba(255, 145, 77, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px rgba(255, 145, 77, 0.38);
  }
`;

const features = [
  { icon: "📊", label: "Real-time project & task tracking" },
  { icon: "💰", label: "Budget and cost oversight" },
  { icon: "🏗️", label: "Built for construction teams" },
];

const DashboardIllustration = () => (
  <Illustration aria-hidden="true">
    <svg viewBox="0 0 420 300" width="100%" xmlns="http://www.w3.org/2000/svg">
      {/* screen frame */}
      <rect x="4" y="4" width="412" height="292" rx="20" fill="#0f2c3d" stroke="rgba(255,255,255,0.08)" />
      {/* window chrome */}
      <circle cx="28" cy="28" r="5" fill="#ff6b6b" />
      <circle cx="46" cy="28" r="5" fill="#ffd166" />
      <circle cx="64" cy="28" r="5" fill="#6ee7b7" />

      {/* sidebar */}
      <rect x="20" y="52" width="70" height="216" rx="12" fill="rgba(255,255,255,0.06)" />
      <rect x="34" y="72" width="42" height="8" rx="4" fill="#3dccc7" />
      <rect x="34" y="96" width="42" height="8" rx="4" fill="rgba(255,255,255,0.25)" />
      <rect x="34" y="120" width="42" height="8" rx="4" fill="rgba(255,255,255,0.25)" />
      <rect x="34" y="144" width="42" height="8" rx="4" fill="rgba(255,255,255,0.25)" />

      {/* stat pill */}
      <rect x="108" y="52" width="120" height="52" rx="12" fill="rgba(61,204,199,0.14)" stroke="rgba(61,204,199,0.4)" />
      <text x="120" y="76" fill="#7fe6e1" fontSize="11" fontWeight="700" fontFamily="Arial, sans-serif">PROJECTS</text>
      <text x="120" y="95" fill="#ffffff" fontSize="18" fontWeight="800" fontFamily="Arial, sans-serif">128</text>

      <rect x="240" y="52" width="150" height="52" rx="12" fill="rgba(255,145,77,0.14)" stroke="rgba(255,145,77,0.4)" />
      <text x="252" y="76" fill="#ffb27f" fontSize="11" fontWeight="700" fontFamily="Arial, sans-serif">BUDGET USED</text>
      <text x="252" y="95" fill="#ffffff" fontSize="18" fontWeight="800" fontFamily="Arial, sans-serif">62%</text>

      {/* bar chart */}
      <rect x="108" y="120" width="164" height="148" rx="14" fill="rgba(255,255,255,0.05)" />
      <rect x="128" y="220" width="18" height="30" rx="4" fill="#3dccc7" />
      <rect x="154" y="196" width="18" height="54" rx="4" fill="#3dccc7" />
      <rect x="180" y="170" width="18" height="80" rx="4" fill="#7fe6e1" />
      <rect x="206" y="204" width="18" height="46" rx="4" fill="#3dccc7" />
      <rect x="232" y="150" width="18" height="100" rx="4" fill="#ff914d" />

      {/* donut chart */}
      <rect x="288" y="120" width="102" height="148" rx="14" fill="rgba(255,255,255,0.05)" />
      <circle cx="339" cy="178" r="34" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="14" />
      <circle
        cx="339"
        cy="178"
        r="34"
        fill="none"
        stroke="#3dccc7"
        strokeWidth="14"
        strokeDasharray="160 214"
        strokeLinecap="round"
        transform="rotate(-90 339 178)"
      />
      <text x="339" y="183" fill="#fff" fontSize="16" fontWeight="800" textAnchor="middle" fontFamily="Arial, sans-serif">74%</text>
      <text x="339" y="238" fill="#cbd8e0" fontSize="10.5" textAnchor="middle" fontFamily="Arial, sans-serif">On schedule</text>
    </svg>
  </Illustration>
);

const ConstructionDashboardPromo = () => {
  return (
    <Section>
      <Card>
        <DashboardIllustration />

        <TextBlock>
          <Title>Universal Construction Dashboard</Title>
          <Desc>
            A ready-to-use dashboard for construction teams to track projects, budgets, and
            progress in one place. See it live and explore how it works.
          </Desc>

          <Features>
            {features.map((f) => (
              <Feature key={f.label}>
                <FeatureIcon>{f.icon}</FeatureIcon>
                {f.label}
              </Feature>
            ))}
          </Features>

          <CTA href={CONSTRUCTION_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
            Open Dashboard ↗
          </CTA>
        </TextBlock>
      </Card>
    </Section>
  );
};

export default ConstructionDashboardPromo;
